/**
 * Componente: Modal Especial de Selección de Prefijo de WhatsApp — PrefijoWhatsappModalSelector
 * Diseñado con la misma ergonomía, microanimaciones y accesibilidad que PaisModalSelector y CiudadModalSelector,
 * pero especializado para canales de mensajería (WhatsApp) con la paleta de marca (#25D366 / #70a32c).
 *
 * - Fondo blur centrado neutro (backdrop-filter: blur(16px)).
 * - Alimentado por `country-state-city` y `country-flag-icons`.
 * - Búsqueda instantánea por país, código ISO o número de prefijo (+57, 57, Col...).
 * - Badge de prefijo en monoespaciado y checkmark de confirmación.
 */

'use client';

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Country } from 'country-state-city';
import * as Flags from 'country-flag-icons/react/3x2';
import { hasFlag } from 'country-flag-icons';
import { WhatsAppIcon } from '@/shared/components/ui/icons/WhatsAppIcon';
import styles from './PrefijoWhatsappModalSelector.module.css';

export interface PrefijoWhatsappItem {
  name: string;        // Nombre en español (ej. "Colombia", "México", "España")
  englishName: string; // Nombre original en country-state-city (ej. "Colombia", "Mexico")
  isoCode: string;     // Código ISO (ej. "CO", "MX", "ES")
  phoneCode: string;   // Prefijo normalizado con signo + (ej. "+57", "+52", "+34")
}

export interface PrefijoWhatsappModalSelectorProps {
  isOpen: boolean;
  selectedDialCode?: string; // ej. "+57"
  selectedIsoCode?: string;  // ej. "CO"
  onSelect: (item: PrefijoWhatsappItem) => void;
  onClose: () => void;
}

// Países prioritarios en el orden de mayor uso para la plataforma avícola
const PRIORITY_ISOS = [
  'CO', // Colombia (+57)
  'MX', // México (+52)
  'PE', // Perú (+51)
  'EC', // Ecuador (+593)
  'BR', // Brasil (+55)
  'AR', // Argentina (+54)
  'CL', // Chile (+56)
  'ES', // España (+34)
  'US', // Estados Unidos (+1)
  'VE', // Venezuela (+58)
  'BO', // Bolivia (+591)
  'UY', // Uruguay (+598)
  'PY', // Paraguay (+595)
  'CR', // Costa Rica (+506)
  'PA', // Panamá (+507)
  'GT', // Guatemala (+502)
  'DO', // Rep. Dominicana (+1809)
  'SV', // El Salvador (+503)
  'HN', // Honduras (+504)
  'NI', // Nicaragua (+505)
  'CA', // Canadá (+1)
];

// Instancia nativa para resolver nombres de países en español
let spanishRegionNames: Intl.DisplayNames | null = null;
try {
  spanishRegionNames = new Intl.DisplayNames(['es'], { type: 'region' });
} catch {
  spanishRegionNames = null;
}

function getSpanishCountryName(isoCode: string, defaultName: string): string {
  if (spanishRegionNames) {
    try {
      const translated = spanishRegionNames.of(isoCode);
      if (translated) return translated;
    } catch {
      // Usar nombre por defecto si no hay traducción
    }
  }
  return defaultName;
}

/**
 * Renderizador de bandera oficial SVG mediante `country-flag-icons`
 */
function CountryFlagIcon({ isoCode }: { isoCode: string }) {
  const upper = (isoCode || '').toUpperCase();
  if (hasFlag(upper)) {
    const FlagComp = (
      Flags as Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>>
    )[upper];
    if (FlagComp) {
      return <FlagComp className="w-full h-full object-cover" />;
    }
  }
  return <span className="text-[11px] leading-none select-none">🌐</span>;
}

export function PrefijoWhatsappModalSelector({
  isOpen,
  selectedDialCode = '+57',
  selectedIsoCode = 'CO',
  onSelect,
  onClose,
}: PrefijoWhatsappModalSelectorProps) {
  const [search, setSearch] = useState('');
  const [mounted, setMounted] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Cargar catálogo completo con prefijo telefónico normalizado
  const allCountries: PrefijoWhatsappItem[] = useMemo(() => {
    const rawList = Country.getAllCountries();
    const list: PrefijoWhatsappItem[] = [];

    for (const c of rawList) {
      const rawPhone = (c.phonecode || '').trim();
      if (!rawPhone) continue;

      const formattedPhone = rawPhone.startsWith('+') ? rawPhone : `+${rawPhone}`;

      list.push({
        name: getSpanishCountryName(c.isoCode, c.name),
        englishName: c.name,
        isoCode: c.isoCode,
        phoneCode: formattedPhone,
      });
    }

    // Ordenar: primero los países prioritarios en su orden de diseño, luego el resto alfabéticamente
    list.sort((a, b) => {
      const idxA = PRIORITY_ISOS.indexOf(a.isoCode);
      const idxB = PRIORITY_ISOS.indexOf(b.isoCode);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.name.localeCompare(b.name, 'es');
    });

    return list;
  }, []);

  // Filtrado reactivo en tiempo real (por nombre, inglés, código ISO o dígitos del prefijo)
  const filteredCountries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return allCountries;

    const queryClean = query.replace('+', '');

    return allCountries.filter((c) => {
      const codeClean = c.phoneCode.replace('+', '');
      return (
        c.name.toLowerCase().includes(query) ||
        c.englishName.toLowerCase().includes(query) ||
        c.isoCode.toLowerCase() === query ||
        c.phoneCode.toLowerCase().includes(query) ||
        (queryClean.length > 0 && codeClean.startsWith(queryClean))
      );
    });
  }, [allCountries, search]);

  // Manejo de foco automático al abrir
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Cerrar con tecla Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  const currentSelectedIso = (selectedIsoCode || '').trim().toUpperCase();
  const currentSelectedDial = (selectedDialCode || '').trim();

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="whatsapp-prefix-modal-title"
    >
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botón Cerrar (X) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors cursor-pointer focus:outline-none z-10"
          aria-label="Cerrar modal"
        >
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>

        {/* Encabezado: Icono WhatsApp y Título */}
        <div className="flex items-center gap-3 pr-10">
          <div className={styles.iconBadge}>
            <WhatsAppIcon size={28} />
          </div>
          <div>
            <h2
              id="whatsapp-prefix-modal-title"
              className="text-lg sm:text-2xl font-bold text-gray-900 tracking-tight leading-snug"
            >
              Código de país para WhatsApp
            </h2>
            <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">
              Selecciona tu indicativo para el envío de alertas y notificaciones a tu teléfono.
            </p>
          </div>
        </div>

        {/* Barra de Búsqueda */}
        <div className={styles.searchContainer}>
          <div className={styles.searchIconWrapper}>
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por país o prefijo (+57, Colombia, 52...)"
            className={styles.searchInput}
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                searchInputRef.current?.focus();
              }}
              className={styles.clearSearchBtn}
              aria-label="Borrar búsqueda"
              title="Borrar búsqueda"
            >
              <svg
                className="w-3.5 h-3.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Grilla de Países y Prefijos */}
        <div
          data-scrollable="true"
          className={`w-full flex-1 ${styles.prefixListScroll}`}
        >
          {filteredCountries.length > 0 ? (
            <div className={styles.prefixGrid}>
              {filteredCountries.map((country) => {
                const isSelected =
                  currentSelectedIso === country.isoCode.toUpperCase() ||
                  (!currentSelectedIso && currentSelectedDial === country.phoneCode);

                return (
                  <button
                    key={`${country.isoCode}-${country.phoneCode}`}
                    type="button"
                    onClick={() => {
                      onSelect(country);
                      onClose();
                    }}
                    className={`${styles.prefixButton} ${
                      isSelected ? styles.prefixButtonSelected : ''
                    }`}
                  >
                    {/* Banderita y Nombre del País */}
                    <div className="flex items-center gap-2.5 min-w-0 pr-2 flex-1">
                      <span className={styles.flagBadge}>
                        <CountryFlagIcon isoCode={country.isoCode} />
                      </span>
                      <span
                        className={`text-sm truncate ${
                          isSelected ? 'font-bold text-emerald-950' : 'font-semibold text-gray-800'
                        }`}
                        title={country.name}
                      >
                        {country.name}
                      </span>
                    </div>

                    {/* Pastilla de Prefijo Telefónico y Checkmark */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={styles.dialCodePill}>
                        {country.phoneCode}
                      </span>

                      {isSelected && (
                        <svg
                          className="w-4 h-4 text-emerald-600 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-2">
                <span className="material-symbols-outlined text-[24px]">search_off</span>
              </div>
              <p className="text-sm font-bold text-gray-700">No encontramos resultados</p>
              <p className="text-xs text-gray-500 mt-1 max-w-xs">
                Intenta buscar con otro nombre de país o simplemente el número de prefijo (ej. &quot;57&quot;).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
